import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { authApi } from '../api/auth'
import { UserContextResponse, ContextSelectionRequest, UserContext, NivelAcesso } from '../types/auth'
import { useAuth } from '../contexts/AuthContext'

interface ChangeContextDialogProps {
  open: boolean
  onClose: () => void
}

const ChangeContextDialog: React.FC<ChangeContextDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation()
  const { user, updateContext } = useAuth()
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [context, setContext] = useState<UserContextResponse | null>(null)
  const [selectedRole, setSelectedRole] = useState<'SUPER_ADMIN' | 'ADMINISTRADOR' | 'USUARIO' | ''>('')
  const [selectedEmpresaId, setSelectedEmpresaId] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Buscar contexto atual do usuário
  const { data: contextData, isLoading: isLoadingContext } = useQuery({
    queryKey: ['user-context'],
    queryFn: () => authApi.getUserContext(),
    enabled: open && !!user,
  })

  useEffect(() => {
    if (contextData) {
      setContext(contextData)
      setLoading(false)
      
      // Carregar contexto atual do localStorage
      const currentContextStr = localStorage.getItem('user_context')
      if (currentContextStr) {
        try {
          const currentContext = JSON.parse(currentContextStr) as UserContext
          setSelectedRole(currentContext.role)
          setSelectedEmpresaId(currentContext.empresa_id ?? null)
        } catch (e) {
          // Ignorar erro
        }
      }
    }
  }, [contextData])

  const handleSubmit = async () => {
    if (!selectedRole) {
      setError(t('context.selectMode'))
      return
    }

    // Validar se precisa de empresa
    const needsEmpresa = 
      selectedRole === 'USUARIO' || 
      selectedRole === 'ADMINISTRADOR'
    
    if (needsEmpresa && !selectedEmpresaId) {
      setError(t('context.selectCompanyError'))
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      // Calcular nível de acesso efetivo
      let nivel_acesso_efetivo = context!.nivel_acesso
      if (selectedRole === 'USUARIO') {
        nivel_acesso_efetivo = NivelAcesso.USUARIO_PADRAO
      } else if (selectedRole === 'ADMINISTRADOR') {
        nivel_acesso_efetivo = NivelAcesso.ADMINISTRADOR
      } else if (selectedRole === 'SUPER_ADMIN') {
        nivel_acesso_efetivo = NivelAcesso.SUPER_ADMIN
      }

      // Determinar empresa_id baseado no role
      let empresaIdForContext: number | null = null
      if (selectedRole === 'USUARIO') {
        empresaIdForContext = selectedEmpresaId || null
      } else if (selectedRole === 'ADMINISTRADOR') {
        empresaIdForContext = selectedEmpresaId || null
      } else if (selectedRole === 'SUPER_ADMIN') {
        empresaIdForContext = null
      }

      const contextSelection: ContextSelectionRequest = {
        role: selectedRole,
        empresa_id: empresaIdForContext,
      }

      await authApi.selectContext(contextSelection)

      // Armazenar contexto completo
      const selectedEmpresa = context!.empresas.find(e => e.id_empresa === empresaIdForContext)
      const userContext: UserContext = {
        role: selectedRole,
        empresa_id: empresaIdForContext,
        nivel_acesso_efetivo,
        preferencias: selectedEmpresa?.preferencias || {},
        empresa_nome: selectedEmpresa?.nome_empresa || null,
        empresa_cnpj: selectedEmpresa?.cnpj || null
      }
      localStorage.setItem('user_context', JSON.stringify(userContext))

      // Atualizar contexto no AuthContext
      updateContext(userContext)

      // Atualizar contexto no AuthContext (já invalida queries automaticamente)
      updateContext(userContext)

      // Invalidar query de contexto também
      queryClient.invalidateQueries({ queryKey: ['user-context'] })

      // Fechar diálogo
      onClose()
    } catch (err: any) {
      setError(err.response?.data?.detail || t('context.saveError'))
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!submitting) {
      setError(null)
      onClose()
    }
  }

  if (isLoadingContext || loading) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    )
  }

  if (!context) {
    return null
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('context.changeTitle')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
            <FormLabel component="legend">{t('context.modeOfAction')}</FormLabel>
            <RadioGroup
              value={selectedRole}
              onChange={(e) => {
                setSelectedRole(e.target.value as 'SUPER_ADMIN' | 'ADMINISTRADOR' | 'USUARIO')
                if (e.target.value === 'SUPER_ADMIN') {
                  setSelectedEmpresaId(null)
                }
              }}
            >
              {context.can_act_as_super_admin && (
                <FormControlLabel
                  value="SUPER_ADMIN"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {t('context.superAdmin')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('context.superAdminDescription')}
                      </Typography>
                    </Box>
                  }
                />
              )}
              {context.can_act_as_admin && (
                <FormControlLabel
                  value="ADMINISTRADOR"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {t('context.administrator')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('context.administratorDescription')}
                      </Typography>
                    </Box>
                  }
                />
              )}
              {context.can_act_as_user && (
                <FormControlLabel
                  value="USUARIO"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {t('context.user')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('context.userDescription')}
                      </Typography>
                    </Box>
                  }
                />
              )}
            </RadioGroup>
          </FormControl>

          {/* Mostrar seleção de empresa para USUARIO ou ADMINISTRADOR */}
          {(selectedRole === 'USUARIO' || selectedRole === 'ADMINISTRADOR') && (
            <>
              {context.empresas && context.empresas.length > 0 ? (
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="empresa-select-label">{t('context.companyRequired')}</InputLabel>
                  <Select
                    labelId="empresa-select-label"
                    value={selectedEmpresaId || ''}
                    label={t('context.companyRequired')}
                    onChange={(e) => setSelectedEmpresaId(Number(e.target.value))}
                    required
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      <em>{t('context.selectCompany')}</em>
                    </MenuItem>
                    {context.empresas.map((empresa) => (
                      <MenuItem key={empresa.id_empresa} value={empresa.id_empresa}>
                        {empresa.nome_empresa}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  {t('context.noCompanyAvailable')}
                </Alert>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || !selectedRole}
        >
          {submitting ? <CircularProgress size={24} /> : t('context.changeContext')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ChangeContextDialog
